import { cn } from "@/lib/utils";
import {
  segmentedTrackClass,
  segmentedTriggerClass,
} from "./segmented-variants";

export type TabsVariant = "panel" | "segmented" | "underline";

/**
 * Hide the scrollbar of horizontally scrolling tab rows only where swiping is the
 * scroll affordance (narrow or touch); desktop mice keep a visible scrollbar.
 */
const TOUCH_SCROLLBAR_HIDDEN_CLASS =
  "max-sm:[scrollbar-width:none] max-sm:[&::-webkit-scrollbar]:hidden pointer-coarse:[scrollbar-width:none] pointer-coarse:[&::-webkit-scrollbar]:hidden";
export type TabsLayout = "equal" | "wrap";

export function tabListClass(
  variant: TabsVariant,
  layout: TabsLayout = "equal",
): string {
  if (variant === "panel") {
    return cn(
      "mb-6 w-full gap-1 rounded-xl border border-surface-200 bg-surface-100/70 p-1",
      layout === "wrap"
        ? "flex flex-wrap"
        : cn("grid max-sm:flex max-sm:overflow-x-auto", TOUCH_SCROLLBAR_HIDDEN_CLASS),
    );
  }
  if (variant === "segmented") {
    return cn("flex flex-wrap gap-1", segmentedTrackClass);
  }
  // Baseline as an inset shadow (painted inside the scroll box) instead of a border:
  // `overflow-x-auto` clips anything outside the padding box, so triggers cannot
  // overlap a list border with `-mb-px`.
  return cn(
    "flex flex-nowrap gap-1 overflow-x-auto shadow-[inset_0_-1px_0_var(--color-surface-200)]",
    TOUCH_SCROLLBAR_HIDDEN_CLASS,
  );
}

export function tabTriggerClass(
  variant: TabsVariant,
  active: boolean,
  layout: TabsLayout = "equal",
): string {
  if (variant === "panel") {
    return cn(
      "flex min-w-0 shrink-0 flex-col items-center gap-0.5 rounded-lg px-3 py-2 text-center text-sm transition-all max-sm:min-w-max",
      layout === "wrap" && "min-w-36 flex-1 sm:min-w-0",
      active
        ? "bg-surface-0 text-surface-900 shadow-xs ring-1 ring-surface-200 dark:bg-surface-200 dark:ring-surface-300/60"
        : "text-surface-500 hover:bg-surface-0/60 hover:text-surface-800 dark:hover:bg-surface-200/50",
    );
  }
  if (variant === "segmented") {
    return segmentedTriggerClass(active);
  }
  return cn(
    "inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
    active
      ? "border-primary-600 text-surface-900"
      : "border-transparent text-surface-500 hover:border-surface-300 hover:text-surface-800",
  );
}
